import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { ArrowRightIconComponent } from '../../../app/core/svg-icons/arrow-right-icon.component';

const meta: Meta<ArrowRightIconComponent> = {
  title: 'Atoms/Icons/ArrowRightIcon',
  component: ArrowRightIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A right-pointing arrow icon component. Perfect for navigation, call-to-action buttons, and indicating direction or next steps.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<ArrowRightIconComponent>;

export const Default: Story = {
  render: () => ({
    template: '<app-arrow-right-icon></app-arrow-right-icon>',
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};

export const InButton: Story = {
  render: () => ({
    template: `
      <button class="btn btn-primary-supervin" style="display: flex; align-items: center; gap: 0.5rem;">
        <span>Se mere</span>
        <app-arrow-right-icon></app-arrow-right-icon>
      </button>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Arrow right icon used in a call-to-action button.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};

export const InNavigation: Story = {
  render: () => ({
    template: `
      <nav style="border: 1px solid #ddd; border-radius: 8px; padding: 1rem;">
        <div style="display: flex; flex-direction: column; gap: 0.5rem;">
          <a href="#" style="display: flex; align-items: center; justify-content: space-between; text-decoration: none; color: #333; padding: 0.5rem;">
            <span>Rødvine</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </a>
          <a href="#" style="display: flex; align-items: center; justify-content: space-between; text-decoration: none; color: #333; padding: 0.5rem;">
            <span>Hvidvine</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </a>
          <a href="#" style="display: flex; align-items: center; justify-content: space-between; text-decoration: none; color: #333; padding: 0.5rem;">
            <span>Champagne</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </a>
        </div>
      </nav>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Arrow right icons used in navigation menu to indicate clickable items.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};

export const InBreadcrumb: Story = {
  render: () => ({
    template: `
      <nav aria-label="breadcrumb">
        <ol style="display: flex; align-items: center; gap: 0.5rem; list-style: none; padding: 0; margin: 0;">
          <li><a href="#" style="text-decoration: none; color: #5C0A31;">Hjem</a></li>
          <li><app-arrow-right-icon></app-arrow-right-icon></li>
          <li><a href="#" style="text-decoration: none; color: #5C0A31;">Vine</a></li>
          <li><app-arrow-right-icon></app-arrow-right-icon></li>
          <li><a href="#" style="text-decoration: none; color: #5C0A31;">Rødvine</a></li>
          <li><app-arrow-right-icon></app-arrow-right-icon></li>
          <li><span style="color: #666;">Barolo 2018</span></li>
        </ol>
      </nav>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Arrow right icons used as separators in breadcrumb navigation.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};

export const InProductCard: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1rem; max-width: 300px;">
        <div style="background-color: #f8f9fa; height: 150px; margin-bottom: 1rem; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #666;">
          [Vin billede]
        </div>
        <h5>Barolo DOCG 2018</h5>
        <p style="color: #666; margin: 0.5rem 0;">Piemont, Italien</p>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <strong style="color: #5C0A31;">299 kr</strong>
          <button class="btn btn-outline-primary" style="display: flex; align-items: center; gap: 0.25rem; padding: 0.25rem 0.5rem;">
            <span style="font-size: 0.875rem;">Se vin</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </button>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Arrow right icon used in a product card to indicate view details action.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};

export const MultipleDirections: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center; flex-wrap: wrap;">
        <div style="text-align: center;">
          <div>Next Step</div>
          <button class="btn btn-primary-supervin" style="display: flex; align-items: center; gap: 0.5rem;">
            <span>Fortsæt</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </button>
        </div>
        <div style="text-align: center;">
          <div>Learn More</div>
          <a href="#" style="display: flex; align-items: center; gap: 0.5rem; text-decoration: none; color: #5C0A31;">
            <span>Læs mere</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </a>
        </div>
        <div style="text-align: center;">
          <div>Navigate</div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span>Næste side</span>
            <app-arrow-right-icon></app-arrow-right-icon>
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different use cases for the arrow right icon indicating forward movement or progression.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-arrow-right-icon')).toBeTruthy();
  },
};
